// Pure extension → app resolution for ctx.shell.openPath.
//
// "Which app opens this file" must give the SAME answer every time: a file that
// opens in Notepad now and Code Studio after a package refresh is worse than one
// that never opens at all. So the choice is a total order over candidates
// (dock.order, then id) rather than whatever `packages` happened to be sorted by.
// No imports: this runs in the browser bundle and under `node --test` unchanged.
//
// KEEP IN SYNC with ./file-assoc.d.mts (hand-written type mirror).

/**
 * The lowercased extension of `path`, WITH the leading dot — the same spelling
 * manifests use, so `manifest.fileTypes.includes(extensionOf(p))` just works.
 *
 * Returns "" when there is nothing to match on:
 *  - no dot at all (`README`)
 *  - a trailing dot (`archive.`)
 *  - a dotfile whose only dot starts the basename (`.bashrc`) — that dot names
 *    the file, it does not describe a type, and treating it as `.bashrc` would
 *    let an app claim every dotfile by declaring one.
 *
 * @param {string} path
 * @returns {string}
 */
export function extensionOf(path) {
  const p = typeof path === "string" ? path.trim() : "";
  if (!p) return "";
  const base = p.slice(p.lastIndexOf("/") + 1);
  const dot = base.lastIndexOf(".");
  // dot < 1 covers both "no dot" (-1) and the leading-dot dotfile (0).
  if (dot < 1 || dot === base.length - 1) return "";
  return base.slice(dot).toLowerCase();
}

/**
 * Rank a candidate for a stable tie-break: lower dock.order first, then id
 * ascending. Apps without a dock.order sort after every app that has one.
 * @param {{ manifest?: { id?: string, dock?: { order?: number } } }} rec
 * @returns {[number, string]}
 */
function rank(rec) {
  const order = rec?.manifest?.dock?.order;
  return [typeof order === "number" ? order : Number.MAX_SAFE_INTEGER, String(rec?.manifest?.id || "")];
}

/**
 * Every enabled app whose manifest declares `ext`, best candidate first.
 * Case-insensitive on both sides: a manifest that wrote ".TXT" still matches.
 *
 * @param {Array<{ state?: string, manifest?: { id?: string, kind?: string, fileTypes?: string[], dock?: { order?: number } } }>} packages
 * @param {string} ext extension with the dot, any case
 * @returns {string[]} app ids
 */
export function appsForExtension(packages, ext) {
  const want = typeof ext === "string" ? ext.trim().toLowerCase() : "";
  if (!want || !Array.isArray(packages)) return [];
  const hits = packages.filter((rec) => {
    if (!rec || rec.state !== "enabled") return false;
    const m = rec.manifest;
    if (!m || !m.id || m.kind !== "app") return false;
    const types = m.fileTypes;
    if (!Array.isArray(types)) return false;
    return types.some((t) => typeof t === "string" && t.trim().toLowerCase() === want);
  });
  hits.sort((a, b) => {
    const [ao, aid] = rank(a);
    const [bo, bid] = rank(b);
    return ao - bo || aid.localeCompare(bid);
  });
  return hits.map((rec) => String(rec.manifest.id));
}

/**
 * The single app that should open `path`, or null when nothing claims its type.
 * Null is an ordinary answer here — the caller says "no app handles .xyz"; it is
 * not an error condition.
 *
 * @param {Array<{ state?: string, manifest?: { id?: string, kind?: string, fileTypes?: string[], dock?: { order?: number } } }>} packages
 * @param {string} path
 * @returns {string | null}
 */
export function resolveAppForPath(packages, path) {
  const ext = extensionOf(path);
  if (!ext) return null;
  return appsForExtension(packages, ext)[0] ?? null;
}
